import { requestWithDeadline } from './requestDeadline';
import { setGlobalLoading } from '../components/ui/ProgressBar';
import { API_BASE } from './apiBase';
import { sessionState } from './sessionState';

let activeRequests = 0;
let _toastFn = null;
export function setGlobalToast(fn) { _toastFn = fn; }

export async function apiFetch(path, options = {}) {
  activeRequests++;
  if (activeRequests === 1) setGlobalLoading(true);

  try {
    const session = sessionState();
    const headers = {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    };
    return await requestWithDeadline(`${API_BASE}${path}`, { ...options, headers, credentials: 'include' }, async res => {
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('Non-JSON response from', path, res.status);
        return { ok: false, message: `Server error (${res.status})` };
      }
      const data = await res.json();
      if (res.status === 403 && data.code === 'MFA_REQUIRED' && session.revision === sessionState().revision) window.dispatchEvent(new Event('scheduler-mfa-required'));
      if (res.status === 428 && data.code === 'LEGAL_ACCEPTANCE_REQUIRED' && session.revision === sessionState().revision) {
        window.dispatchEvent(new Event('scheduler-legal-required'));
      }
      if (res.status === 401 && session.signedIn && session.revision === sessionState().revision && data.code !== 'INVALID_CREDENTIALS') {
        window.dispatchEvent(new Event('scheduler-session-expired'));
        return { ok: false, message: data.message || 'Session expired. Please log in again.' };
      }
      return data;
    });
  } catch (err) {
    if (err.name === 'AbortError') return { ok: false, cancelled: true, message: 'Request cancelled.' };
    const message = err.name === 'TimeoutError'
      ? (options.method && options.method !== 'GET' ? 'The server took too long to respond. Check whether your change was saved before trying again.' : 'The server took too long to respond. Please try again.')
      : 'Network error. Check your connection and try again.';
    if (_toastFn) _toastFn(message, 'error');
    return { ok: false, message, timedOut: err.name === 'TimeoutError' };
  } finally {
    activeRequests--;
    if (activeRequests === 0) setGlobalLoading(false);
  }
}

// Authenticated file download (e.g. CSV export). Uses the HttpOnly session cookie
// and triggers a browser download from the response blob.
export async function apiDownload(path, filename) {
  const blob = await requestWithDeadline(`${API_BASE}${path}`, {
    credentials: 'include', timeoutMs: 60000,
  }, async res => {
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    return res.blob();
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Authenticated multipart upload (FormData). Lets the browser set the multipart
// boundary — do NOT set Content-Type here.
export async function apiUpload(path, formData) {
  try {
    return await requestWithDeadline(`${API_BASE}${path}`, {
      timeoutMs: 60000,
      method: 'POST',
      credentials: 'include',
      body: formData,
    }, async res => {
      const ct = res.headers.get('content-type');
      if (!ct || !ct.includes('application/json')) return { ok: false, message: `Server error (${res.status})` };
      return res.json();
    });
  } catch (error) {
    if (error.name === 'TimeoutError') return { ok: false, message: 'Upload timed out. Check whether the file was saved before trying again.' };
    return { ok: false, message: 'Network error. Please try again.' };
  }
}

// Auth
export const login = (email, password, rememberMe = true) =>
  apiFetch('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password, rememberMe, sessionMode: 'cookie' }),
  });

// Restaurants
export const getRestaurants = () => apiFetch('/api/restaurants');

export const createRestaurant = (data) =>
  apiFetch('/api/restaurants', {
    method: 'POST',
    body: JSON.stringify(data),
  });

// Employees
export const getEmployees = (restaurantId) =>
  apiFetch(`/api/${restaurantId}/employees`);

export const addEmployee = (restaurantId, data) =>
  apiFetch(`/api/${restaurantId}/employees`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const toggleEmployee = (restaurantId, id, active) =>
  apiFetch(`/api/${restaurantId}/employees/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ active }),
  });

// Managers
export const getManagers = () => apiFetch('/api/managers');

export const createManager = (data) =>
  apiFetch('/api/managers', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const toggleManager = (id, active) =>
  apiFetch(`/api/managers/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ active }),
  });

  // Departments
export const getDepartments = (restaurantId) =>
  apiFetch(`/api/${restaurantId}/departments`);

export const addDepartment = (restaurantId, data) =>
  apiFetch(`/api/${restaurantId}/departments`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const deleteDepartment = (restaurantId, id) =>
  apiFetch(`/api/${restaurantId}/departments/${id}`, { method: 'DELETE' });

// Schedules
export const getSchedule = (restaurantId, weekStart) =>
  apiFetch(`/api/${restaurantId}/schedules?weekStart=${weekStart}`);

export const createShift = (restaurantId, data) =>
  apiFetch(`/api/${restaurantId}/schedules`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const updateShift = (restaurantId, id, data) =>
  apiFetch(`/api/${restaurantId}/schedules/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const deleteShift = (restaurantId, id) =>
  apiFetch(`/api/${restaurantId}/schedules/${id}`, { method: 'DELETE' });

export const publishWeek = (restaurantId, weekStart) =>
  apiFetch(`/api/${restaurantId}/schedules/publish`, {
    method: 'PATCH',
    body: JSON.stringify({ weekStart }),
  });

export const setRestaurantStatus = (id, active) =>
  apiFetch(`/api/admin/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ active }),
  });

export const getAllEmployees = () => apiFetch('/api/employees/all');

export const moveEmployeeLocation = (employeeId, restaurantId, expectedRestaurantId) =>
  apiFetch(`/api/employees/${employeeId}/location`, {
    method: 'PATCH',
    body: JSON.stringify({ restaurantId, expectedRestaurantId }),
  });
